import type { MultiValidator, Validations } from '../utils/validate';

import InvalidFormError from '../utils/InvalidFormError';
import createFormAction from '../utils/createFormAction';
import createInputAction from '../utils/createInputAction';
import { forEach } from 'lodash';
import { noop } from 'lodash';
import { validate } from '../utils/validate';

/* ACTION TYPES */
// Input Action Types.
const ADD_INPUT = 'nucleus-form/input/ADD_INPUT';
const REMOVE_INPUT = 'nucleus-form/input/REMOVE_INPUT';
const CHANGE_INPUT = 'nucleus-form/input/CHANGE_INPUT';
const FOCUS_INPUT = 'nucleus-form/input/FOCUS_INPUT';
const BLUR_INPUT = 'nucleus-form/input/BLUR_INPUT';
const SET_INPUT_VALIDATIONS = 'nucleus-form/input/SET_INPUT_VALIDATIONS';
const SET_INPUT_VALIDATION_VALUE = 'nucleus-form/input/SET_INPUT_VALIDATION_VALUE';
const VALIDATE_INPUT = 'nucleus-form/input/VALIDATE_INPUT';

// Form Action Types.
const START_SUBMIT_FORM = 'nucleus-form/START_SUBMIT_FORM';
const COMPLETE_SUBMIT_FORM = 'nucleus-form/COMPLETE_SUBMIT_FORM';
const FAIL_SUBMIT_FORM = 'nucleus-form/FAIL_SUBMIT_FORM';

/* ACTION CREATORS */
/**
 * Creates an action to add a new input to the form. If the
 * input exists then nothing will happen.
 */
const addInput = (formId, inputId) => {
  return createInputAction(ADD_INPUT, formId, inputId);
};

/**
 * Creates an action to mark the input as changed.
 */
const changeInput = (formId, inputId) => {
  return createInputAction(CHANGE_INPUT, formId, inputId);
};

/**
 * Creates an action to mark the input as focused.
 */
const focusInput = (formId, inputId) => {
  return createInputAction(FOCUS_INPUT, formId, inputId);
};

/**
 * Creates an action to mark the input as no longer focused and touched.
 */
const blurInput = (formId, inputId) => {
  return createInputAction(BLUR_INPUT, formId, inputId);
};

/**
 * Creates an action to store an inputs validations for the specified form. These validations
 * can be called prior to form submission. The validations can either be a set of
 * validations or a function that will provide the status of individual validations.
 */
const setInputValidations = (formId, inputId, validations: Validations | MultiValidator) => {
  return createInputAction(SET_INPUT_VALIDATIONS, formId, inputId, { validations });
};

/**
 * Creates an action to set the value for validation of an input for the specified form.
 */
const setInputValidationValue = (formId, inputId, value) => {
  return createInputAction(SET_INPUT_VALIDATION_VALUE, formId, inputId, { value });
};

/**
 * Validates the input with the given validations and creates an action indicating
 * the status of each validation for the input in the form. The validations can
 * either be a set of validations or a function that will provde the results
 * of individual validations.
 * @param context - Any additional information that should be supplied to the validators
 *        as a second param.
 */
const validateInput = (formId, inputId, validations: Validations | MultiValidator, value, context) => {
  const results = validate(validations, value, context);
  return createInputAction(VALIDATE_INPUT, formId, inputId, { value, results });
};

/**
 * Creates an action to remove an input from the specified form.
 */
const removeInput = (formId, inputId) => {
  return createInputAction(REMOVE_INPUT, formId, inputId);
};

/**
 * Calls the provided submitCallback if the form is valid. If the form is invalid
 * or the submitCallback throws an exception the form will be marked as a failed
 * submission.
 * A form is considered valid when all validations pass and and at
 * least one change has been made since the failed form submission.
 * @param formSelector - A selector picking out the form from the slice of state.
 * @param submitCallback - A promise based callback called when the form is valid.
 *    If any errors in the callback occur throw an exception to indicate the form
 *    failed to submit.
 * @param options - Optional options to tailor the behavior of submission.
 * @param options.validateBeforeSumbit - A flag to indicate if any stored validations
 *    should be run prior checking the forms validity.
 * @returns {Promise<void>} A promise that resolves when the submission was
 *    succsessful and rejects when unsuccessful.
 * @throws {InvalidFormError} When the form is invalid due to a failing validation.
 */
const submitForm = (formSelector, submitCallback = noop, options = {}) => {
  const { validateBeforeSubmit = true } = options;
  return async (dispatch, getState) => {
    const form = formSelector(getState());
    const formId = form.id;
    dispatch(createFormAction(START_SUBMIT_FORM, formId));
    if (validateBeforeSubmit) {
      // Context is not passed to the validations because we don't know it. If
      // it is required then the input.validations should be a curried method that
      // has access to the context.
      forEach(form.inputs,
        (input, id) => dispatch(validateInput(formId, id, input.validations, input.validationValue)));
    }
    const validatedForm = formSelector(getState());
    if (!validatedForm.isValid) {
      dispatch(createFormAction(FAIL_SUBMIT_FORM, formId));
      throw new InvalidFormError('Invalid inputs found during submit.');
    }
    try {
      await submitCallback();
      dispatch(createFormAction(COMPLETE_SUBMIT_FORM, formId));
    } catch (error) {
      dispatch(createFormAction(FAIL_SUBMIT_FORM, formId));
      throw error;
    }
  };
};

export const actionTypes = {
  ADD_INPUT, CHANGE_INPUT, FOCUS_INPUT, BLUR_INPUT, REMOVE_INPUT,
  SET_INPUT_VALIDATIONS, SET_INPUT_VALIDATION_VALUE, VALIDATE_INPUT,
  START_SUBMIT_FORM, COMPLETE_SUBMIT_FORM, FAIL_SUBMIT_FORM
};

const actions = {
  addInput, changeInput, focusInput, blurInput, removeInput,
  setInputValidations, setInputValidationValue, validateInput,
  submitForm
};
export default actions;
